import React, { useMemo } from 'react';
import { Bubble } from 'tea-component';
import useIntls from '@opm/hooks/useIntls';
import { ITrendItemBase } from '../../types';
import { useTranslateConfig } from '@opm/hooks/useTranslateConfig';
import { getTopicTypeColor, TTopicTypeColorMap } from '../../utils';

export interface ITopicTag {
  label: string;
  level: 'l1' | 'l2';
  style?: React.CSSProperties;
}

interface ITrendTopicTagsProps {
  record: ITrendItemBase | null;
  topicTypeColorMap?: TTopicTypeColorMap;
  /** chip 的 CSS class */
  chipClassName?: string;
}

/**
 * 通用话题 Tags 渲染组件
 *
 * - L1（一级）：使用 cond 颜色映射渲染
 * - L2（二级）：固定灰色 + hover 显示 Tier 1/2 Category tooltip
 */
export default function TrendTopicTags({
  record,
  topicTypeColorMap,
  chipClassName = '',
}: ITrendTopicTagsProps) {
  const { intl, isEn } = useIntls();
  const { translateType } = useTranslateConfig();

  const { topicTypeL1, topicTypeL2 } = useMemo(() => {
    if (!record) return { topicTypeL1: '', topicTypeL2: '' };
    const translate = translateType as
      | ((key: string) => string)
      | undefined;
    const l1 = translate ? translate(record.topic_type_l1) : record.topic_type_l1;
    const l2 = translate ? translate(record.topic_type_l2) : record.topic_type_l2;
    return {
      // 翻译没命中（返回原值）且当前英文环境时，回退到 *_en 字段
      topicTypeL1:
        isEn && l1 === record.topic_type_l1 ? record.topic_type_l1_en : l1,
      topicTypeL2:
        isEn && l2 === record.topic_type_l2 ? record.topic_type_l2_en : l2,
    };
  }, [record, translateType, isEn]);

  if (!record) return null;

  const chipColor = getTopicTypeColor(record, topicTypeColorMap);

  const topicTags: ITopicTag[] = [
    { label: topicTypeL1, level: 'l1' as const },
    { label: topicTypeL2, level: 'l2' as const },
  ]
    .filter((item) => Boolean(item.label))
    .map((item) => {
      let style: React.CSSProperties | undefined = {
        background: '#F5F5F8',
        color: '#000',
      };
      if (item.level === 'l2') {
        style = {
          background: '#F5F5F8',
          color: '#838697',
        };
      } else if (chipColor) {
        style = {
          background: chipColor.backgroundColor,
          color: chipColor.color,
        };
      }
      return { ...item, style };
    });

  if (topicTags.length === 0) return null;

  const tooltipLabelStyle: React.CSSProperties = {
    display: 'inline-block',
    width: 120,
    color: 'rgba(155, 159, 176, 1)',
  };

  return (
    <>
      {topicTags.map(({ label, style, level }) => {
        const chip = (
          <span className={chipClassName} style={style}>
            {label}
          </span>
        );
        if (level !== 'l2')
          return <React.Fragment key={label}>{chip}</React.Fragment>;

        const tooltipContent = (
          <div>
            <div>
              <span style={tooltipLabelStyle}>{intl('Tier 1 Category')}</span>
              {topicTypeL1}
            </div>
            <div>
              <span style={tooltipLabelStyle}>{intl('Tier 2 Category')}</span>
              {topicTypeL2}
            </div>
          </div>
        );
        return (
          <Bubble key={label} dark placement="top" content={tooltipContent}>
            {chip}
          </Bubble>
        );
      })}
    </>
  );
}
